import Enumerable from "linq";
import Moment from "moment";
import { IDocumentEditable } from "..";
import { instanceOfIInitialApplicable, IInitialApplicable, instanceOfIUserDefinitionItem, IUserDefinitionItem, IItem } from "../../../../domain/document/items";
import { HisInformation, HisInformationItem } from "../../../../domain/his";
import { HisItemParameter, InformationKinds, HisItemBehaviorFactory } from "../../../../domain/his/behavior";
import { InitialSettingKinds } from "../../../../domain/document";
import { IHisInformationValue } from "../../../../domain/his/HisInformationValue";
import { Table } from "../../../../domain/system/table";
import { LoggerFactory } from "../../../../utilities/logger";
import { DateTimeStringUtility } from "../../../../domain/system";
import { IConfiguration } from "../../../../domain/configuration";
import { DomainRegistry } from "../../../../domain";

class UserDefinitionTargetInformation {
  itemSetId: number;
  privateId: number;
  userDefinitionKey: string;
  groupName: string;
}

class UserDefinitionItem {
  name: string;
  value: string;
}

/**
 * TODO:refactor
 */
export class InitialSettingFirstApplicator {
  constructor(private document: IDocumentEditable, private _configuration: IConfiguration) {}

  async apply(): Promise<void> {
    const userDefinitionTargets: UserDefinitionTargetInformation[] = [];
    for (let itemSetId = 0; itemSetId < this.document.itemSets.length; itemSetId++) {
      const items = this.document.itemSets[itemSetId].items;
      for (const item of items) {
        if (!instanceOfIInitialApplicable(item) || !item.isInitial) { continue; }
        if (!item.isEmpty) {
          // ワークフローの証明期間から値を格納されている場合がある
          // V1は上書きはしないようなので、挙動を合わせている
          continue;
        }
        if (instanceOfIUserDefinitionItem(item) && !!item.userDefinitionKey) {
          // ユーザ定義
          // V1と挙動を合わせて先頭のみの対応としている
          if (item.initialSettingKind !== InitialSettingKinds.First) { continue; }
          // バックエンドから取得する都合があるので、できるだけまとめて適用させるようにしている
          // グループ指定されていた時に自動選択しないオブジェクトにも反映させる挙動があるため、いったん対象を抽出したのちまとめて処理している
          const info = new UserDefinitionTargetInformation();
          info.itemSetId = itemSetId;
          info.privateId = item.privateId;
          info.userDefinitionKey = item.userDefinitionKey;
          info.groupName = item.groupName;
          userDefinitionTargets.push(info);
        } else {
          // HIS項目
          // V1の挙動を確認したところ、グループ指定されていても連動入力していない模様
          // ユーザ定義の挙動と異なるので微妙だが、V1と同じ挙動にはなっているはず
          const value = this.extractHisInformationValue(this.document.source.hisInformation, item.hisItemParameter, item.initialSettingKind, this.shouldSelectHospitalizationInitialValueHospitalizedOnly(item));
          if (!value) { continue; }
          const cloned = item.clone() as IInitialApplicable;
          cloned.setHisInfoItems(value, false);
          cloned.adjustObjectHeight();
          cloned.adjustFontSize();
          this.document.updateItem(cloned, {
            itemSetId,
          });
        }
      }
    }
    await this.applyUserDefinition(userDefinitionTargets);
  }

  /**
   * 「現在入院時のみ選択」はV1と同様に先頭の指定のみ判定するようにしている
   */
  private shouldSelectHospitalizationInitialValueHospitalizedOnly(item: IInitialApplicable): boolean {
    if (!item || item.initialSettingKind !== InitialSettingKinds.First) { return false; }
    return item.shouldSelectHospitalizationInitialValueHospitalizedOnly || this._configuration.behavior.document.editor.mischievousObjects.hospitalization.shouldSelectHospitalizationInitialItemHospitalizedOnly;
  }

  extractHisInformationValue(info: HisInformation, param: HisItemParameter, kind: InitialSettingKinds, shouldSelectHospitalizationInitialValueHospitalizedOnly: boolean): IHisInformationValue {
    if (!param) { return null; }
    if (param.informationKind === InformationKinds.Disease) {
      return this.extractHisInformationValueFromTable(info.diseaseCollection, param, kind);
    }
    if (param.informationKind === InformationKinds.Hospitalization) {
      if (shouldSelectHospitalizationInitialValueHospitalizedOnly && !this.isHospitalized(info.hospitalizationCollection, kind)) {
        // 入院していないので選択しない
        return null;
      }
      return this.extractHisInformationValueFromTable(info.hospitalizationCollection, param, kind);
    }
    if (param.informationKind === InformationKinds.Operation) {
      return this.extractHisInformationValueFromTable(info.operationCollection, param, kind);
    }
    return null;
  }

  /**
   * V1の処理はすべての項目を見ているが、セットする値が入院中かどうかで判定するでよさそう
   * http://git.pscad.co.jp:8080/gitbucket/documaker/DocuMakerV1.5/blob/develop/src/findex/documaker/print/screen/component/control/InitialDataControl.java#L336
   */
  private isHospitalized(table: Table, kind: InitialSettingKinds): boolean {
    try {
      if (!table || !table.rows || !table.rows.length) { return false; }
      if (kind === InitialSettingKinds.CreateDate) { return false; }

      const rowIndex = (kind === InitialSettingKinds.First) ? 0 : table.rows.length - 1;
      const hisInformationItems: HisInformationItem[] = [];
      for (let index = 0; index < table.headers.length; ++index) {
        const header = table.headers[index];
        const cell = table.rows[rowIndex].cells[index];
        hisInformationItems.push(new HisInformationItem(header.value, cell.text));
      }
      const enterHospDateText = this.getHisItemValue(hisInformationItems, "ENTER_HOSP_DATE_TIME");
      const leaveHospDateText = this.getHisItemValue(hisInformationItems, "LEAVE_HOSP_DATE_TIME");
      LoggerFactory.getLogger().debug("初期選択項目が入院中かどうかを判定します。入院日: " + enterHospDateText + ", 退院日: " + leaveHospDateText);
      const isHospitalized = this.isHospitalizedDate(enterHospDateText, leaveHospDateText);
      LoggerFactory.getLogger().debug("isHospitalized=" + isHospitalized);
      return isHospitalized;
    } catch (e) {
      LoggerFactory.getLogger().error("入院中かどうかの判定処理中にエラーが発生しました。" + e);
      return false;
    }
  }

  private getHisItemValue(items: HisInformationItem[], key: string): string {
    const found = Enumerable.from(items).firstOrDefault(i => i.name === key);
    return !!found
      ? found.value
      : null;
  }

  private isHospitalizedDate(enterHospDateText: string, leaveHospDateText: string): boolean {
    const enterDate = this.toDateValue(enterHospDateText);
    const leaveDate = this.toDateValue(leaveHospDateText);
    const currentDate = this.toDateValue(Moment(new Date()).format("YYYYMMDD"));
    
    if (!enterDate || currentDate.getTime() < enterDate.getTime()) { return false; }
    return !leaveDate || currentDate.getTime() <= leaveDate.getTime();
  }

  private toDateValue(text: string): Date {
    if (!text || DateTimeStringUtility.isEmptyDate(text)) { return null; }
    if (text.length < 8) { throw new Error("日付の書式が不正です。"); }
    const date = DateTimeStringUtility.toDateValue(text.substring(0, 8));
    if (Number.isNaN(date.getTime())) { throw new Error("不正な日付です。"); }
    return date;
  }

  extractHisInformationValueFromTable(table: Table, param: HisItemParameter, kind: InitialSettingKinds): IHisInformationValue {
    if (!table || !table.rows || !table.rows.length) { return null; }
    if (kind === InitialSettingKinds.CreateDate) { return null; }
    const rowIndex = (kind === InitialSettingKinds.First) ? 0 : table.rows.length - 1;
    const hisInformationItems: HisInformationItem[] = [];
    for (let index = 0; index < table.headers.length; ++index) {
      const header = table.headers[index];
      const cell = table.rows[rowIndex].cells[index];
      hisInformationItems.push(new HisInformationItem(header.value, cell.text));
    }
    const behavior = HisItemBehaviorFactory.create(param.key, "Y 年 M 月 D 日", param.objectKind);
    return behavior.getValue(hisInformationItems);
  }

  /**
   * V1の挙動を確認したところ、ユーザ定義はグループ指定されている場合は連動させている模様（HIS項目のほうはグループ連動していない）
   * また、先頭のみ対応していて、末尾には非対応となっている
   * 微妙な挙動ではあるが、V1と同様の挙動をさせる
   */
  private async applyUserDefinition(infos: UserDefinitionTargetInformation[]): Promise<void> {
    const userDefinitionKeys = Enumerable.from(infos).select(i => i.userDefinitionKey).distinct().toArray();
    for (const userDefinitionKey of userDefinitionKeys) {
      const targets = infos.filter(i => i.userDefinitionKey === userDefinitionKey);
      await this.applyUserDefinitionByKey(userDefinitionKey, targets);
    }
  }

  private async applyUserDefinitionByKey(userDefinitionKey: string, targets: UserDefinitionTargetInformation[]): Promise<void> {
    if (!await this.isAvailableUserDefinition(userDefinitionKey)) {
      LoggerFactory.getLogger().warn("対象のユーザ定義が有効ではないため、処理スキップします。Key=" + userDefinitionKey);
      return;
    }
    // 先頭行のデータ取得
    const firstRowItems = await this.getUserDefinitionFirstRowItems(userDefinitionKey);

    // グループ指定なし（オブジェクト個別）
    for (const target of targets.filter(t => !t.groupName)) {
      const item = this.document.itemSets[target.itemSetId].items[target.privateId];
      const cloned = this.createValueAppliedClone(userDefinitionKey, item, firstRowItems);
      if (!cloned) { continue; }
      this.document.updateItem(cloned, {
        itemSetId: target.itemSetId,
      });
    }
    // グループ指定あり
    const applyedGroups: UserDefinitionTargetInformation[] = [];
    for (const target of targets.filter(t => !!t.groupName)) {
      if (applyedGroups.some(g => g.itemSetId === target.itemSetId && g.groupName === target.groupName)) { continue; }
      applyedGroups.push(target);

      for (const item of this.document.itemSets[target.itemSetId].items) {
        if (!instanceOfIUserDefinitionItem(item) || item.groupName !== target.groupName) { continue; }
        const cloned = this.createValueAppliedClone(userDefinitionKey, item, firstRowItems);
        if (!cloned) { continue; }
        this.document.updateItem(cloned, {
          itemSetId: target.itemSetId,
        });
      }
    }
  }

  private createValueAppliedClone(userDefinitionKey: string, item: IItem, firstRowItems: UserDefinitionItem[]): IItem {
    if (!instanceOfIUserDefinitionItem(item) || item.userDefinitionKey !== userDefinitionKey) { return null; }
    const found = Enumerable.from(firstRowItems).firstOrDefault(i => i.name === item.itemName);
    if (!found) { return null; }

    const cloned = item.clone() as IUserDefinitionItem;
    cloned.setUserDefinitionValues([found.value]);
    cloned.adjustObjectHeight();
    cloned.adjustFontSize();
    const linkName = this.getUserDefineLinkName(cloned);
    if (linkName) {
      // イベント経由で更新しているので、同じitemに対しては一度のupdateItemで更新する必要がある
      const relatedValues = this.getLinkedValues(linkName, firstRowItems);
      cloned.setUserDefinitionLinkValues(relatedValues);
    } else {
      cloned.setUserDefinitionLinkValues(null);
    }
    return cloned;
  }

  private async isAvailableUserDefinition(key: string): Promise<boolean> {
    try {
      const repository = DomainRegistry.format.getFormatRepository();
      return await repository.isAvailableUserDefinition(key);
    } catch (e) {
      LoggerFactory.getLogger().error(e);
      return false;
    }
  }

  private async getUserDefinitionFirstRowItems(key: string): Promise<UserDefinitionItem[]> {
    const repository = DomainRegistry.format.getFormatRepository();
    const table = await repository.getUserDefinitionAsLightTable(key);
    if (table.headers.length === 0 || table.rows.length === 0) { return []; }

    const items: UserDefinitionItem[] = [];
    for (let index = 0; index < table.headers.length; ++index) {
      const item = new UserDefinitionItem();
      item.name = table.headers[index].value;
      item.value = table.rows[0].values[index] ? table.rows[0].values[index] : "";
      items.push(item);
    }
    return items;
  }

  private getUserDefineLinkName(source: IUserDefinitionItem): string {
    const linkActions = source.userDefineLinkActions;
    if (!linkActions || linkActions.length === 0) { return null; }
    return linkActions[0].name;
  }

  private getLinkedValues(linkName: string, items: UserDefinitionItem[]): string[] {
    const found = Enumerable.from(items).firstOrDefault(i => i.name === linkName);
    return (!!found && found.value)
      ? [found.value]
      : [];
  }
}
